import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

import { environment } from 'src/environments/environment';
import buildInfoJson from 'src/environments/build-info.json';
import { HelperModalControllerService } from './helper-modal-controller.service';
import { HelperDebugLoggerControllerService, ServerInfo, OtaBundleInfo } from './helper-debug-logger-controller.service';

export type BuildUpdateStatus =
  | 'UP_TO_DATE'
  | 'OPTIONAL_UPDATE'
  | 'FORCE_UPDATE'
  | 'MAINTENANCE'
  | 'OTA_UPDATE_AVAILABLE'
  | 'OTA_DOWNLOADING'
  | 'OTA_READY_RESTART';

export interface OtaState {
  status: 'idle' | 'checking' | 'downloading' | 'ready' | 'error';
  progress: number;
  newBundleVersion?: string;
  bundleUrl?: string;
  checksum?: string;
  errorMessage?: string;
}

/**
 * SemVer Comparison Utility
 * Returns -1 if v1 < v2, 0 if v1 === v2, 1 if v1 > v2
 */
export function compareVersions(v1: string, v2: string): number {
  if (!v1 && !v2) return 0;
  if (!v1) return -1;
  if (!v2) return 1;

  const clean1 = v1.trim().replace(/^v/i, '');
  const clean2 = v2.trim().replace(/^v/i, '');

  const [main1, pre1] = clean1.split('-');
  const [main2, pre2] = clean2.split('-');

  const parts1 = main1.split('.').map((p) => parseInt(p, 10) || 0);
  const parts2 = main2.split('.').map((p) => parseInt(p, 10) || 0);

  const maxLen = Math.max(parts1.length, parts2.length, 3);

  for (let i = 0; i < maxLen; i++) {
    const num1 = parts1[i] ?? 0;
    const num2 = parts2[i] ?? 0;

    if (num1 > num2) return 1;
    if (num1 < num2) return -1;
  }

  if (!pre1 && pre2) return 1;
  if (pre1 && !pre2) return -1;
  if (pre1 && pre2) {
    if (pre1 > pre2) return 1;
    if (pre1 < pre2) return -1;
  }

  return 0;
}

@Injectable({
  providedIn: 'root',
})
export class HelperBuildUpdateControllerService {
  private http = inject(HttpClient);
  private modalService = inject(HelperModalControllerService);
  private debugLogger = inject(HelperDebugLoggerControllerService);

  readonly currentAppVersion = buildInfoJson.version || '1.0.0';
  readonly currentBuildNumber = Number(buildInfoJson.buildNumber || 1);

  updateStatus = signal<BuildUpdateStatus>('UP_TO_DATE');
  isModalActive = signal<boolean>(false);
  otaState = signal<OtaState>({ status: 'idle', progress: 0 });

  private activeModalRef: any = null;
  private isResumeListenerAttached = false;

  constructor() {
    this.initResumeListener();
  }

  /**
   * Checks if the currently installed native app binary is compatible with the OTA bundle
   */
  isNativeVersionCompatible(currentNativeVersion: string, ota?: OtaBundleInfo): boolean {
    if (!ota) return false;
    if (ota.targetNativeVersion === currentNativeVersion) return true;
    if (
      Array.isArray(ota.compatibleNativeVersions) &&
      ota.compatibleNativeVersions.includes(currentNativeVersion)
    ) {
      return true;
    }
    return false;
  }

  /**
   * Evaluates ServerInfo returned by /api/health/info
   */
  async evaluateServerInfo(serverInfo: ServerInfo): Promise<BuildUpdateStatus> {
    const minVersion = serverInfo.minClientVersion || '1.0.0';
    const latestVersion = serverInfo.latestClientVersion || '1.0.0';
    const isMaintenance = serverInfo.isMaintenance === true;
    const currentVer = this.currentAppVersion;
    const isNative = Capacitor.isNativePlatform();

    if (isMaintenance) {
      this.updateStatus.set('MAINTENANCE');
      await this.presentUpdateModal({
        mode: 'MAINTENANCE',
        title: 'System Maintenance',
        message: serverInfo.maintenanceMessage,
        currentVersion: currentVer,
        targetVersion: latestVersion,
        storeUrls: serverInfo.storeUrls,
      });
      return 'MAINTENANCE';
    }

    const ota = serverInfo.ota;
    const isOtaCompatible = isNative && ota && this.isNativeVersionCompatible(currentVer, ota);
    const hasNewerOtaBuild =
      isOtaCompatible &&
      ota &&
      (ota.latestBuildNumber > this.currentBuildNumber ||
        (ota.bundleVersion && compareVersions(currentVer, minVersion) < 0));

    // Tier 1 / Deprecation: If current version is below minClientVersion
    if (compareVersions(currentVer, minVersion) < 0) {
      // Major OTA Resolution: If compatible OTA bundle exists (e.g. Major 2.0.0 OTA for 1.0.0 native)
      if (hasNewerOtaBuild && ota) {
        this.updateStatus.set('OTA_UPDATE_AVAILABLE');

        this.downloadAndApplyOta({ ...ota, isMandatory: true })
          .then((success) => {
            if (!success) {
              this.handle426Upgrade({
                minClientVersion: minVersion,
                latestClientVersion: latestVersion,
                storeUrls: serverInfo.storeUrls,
              });
            }
          })
          .catch(() => {
            this.handle426Upgrade({
              minClientVersion: minVersion,
              latestClientVersion: latestVersion,
              storeUrls: serverInfo.storeUrls,
            });
          });

        return 'OTA_UPDATE_AVAILABLE';
      }

      // No compatible OTA available -> prompt Store Force Update modal
      this.updateStatus.set('FORCE_UPDATE');
      await this.presentUpdateModal({
        mode: 'FORCE_UPDATE',
        title: 'Update Required',
        message: 'This version of the app is no longer supported. Please update to continue.',
        currentVersion: currentVer,
        targetVersion: latestVersion,
        storeUrls: serverInfo.storeUrls,
      });
      return 'FORCE_UPDATE';
    }

    // Tier 2: Check OTA Live Updates for Native Platforms (Major, Minor, or Patch)
    if (hasNewerOtaBuild && ota && ota.latestBuildNumber > this.currentBuildNumber) {
      this.updateStatus.set('OTA_UPDATE_AVAILABLE');

      this.downloadAndApplyOta(ota).catch((err) => {
        console.warn('⚠️ OTA background update failed:', err);
      });
      return 'OTA_UPDATE_AVAILABLE';
    }

    // Tier 3: Check Optional Native Store Update
    if (compareVersions(currentVer, latestVersion) < 0) {
      this.updateStatus.set('OPTIONAL_UPDATE');
      await this.presentUpdateModal({
        mode: 'OPTIONAL_UPDATE',
        title: 'New Update Available',
        message: 'A newer version of the app is available with improvements and new features.',
        currentVersion: currentVer,
        targetVersion: latestVersion,
        storeUrls: serverInfo.storeUrls,
      });
      return 'OPTIONAL_UPDATE';
    }

    // Tier 4: Check Web Chunk / Build Update
    if (!isNative && serverInfo.buildNumber && serverInfo.buildNumber > this.currentBuildNumber) {
      this.updateStatus.set('OPTIONAL_UPDATE');
      return 'OPTIONAL_UPDATE';
    }

    // Up to date
    this.updateStatus.set('UP_TO_DATE');
    this.dismissActiveModalIfOpen();
    return 'UP_TO_DATE';
  }

  /**
   * Downloads and applies an OTA live update bundle
   */
  async downloadAndApplyOta(otaInfo: OtaBundleInfo): Promise<boolean> {
    this.otaState.set({
      status: 'downloading',
      progress: 0,
      newBundleVersion: otaInfo.bundleVersion,
      bundleUrl: otaInfo.bundleUrl,
      checksum: otaInfo.checksum,
    });
    this.updateStatus.set('OTA_DOWNLOADING');

    try {
      await this.downloadBundle(otaInfo);

      this.otaState.update((state) => ({ ...state, status: 'ready', progress: 100 }));
      this.updateStatus.set('OTA_READY_RESTART');

      if (otaInfo.isMandatory) {
        this.applyOtaAndReload();
      }
      return true;
    } catch (err: any) {
      this.otaState.set({
        status: 'error',
        progress: 0,
        errorMessage: err?.message || 'OTA Download failed',
      });
      return false;
    }
  }

  /**
   * Activates downloaded OTA bundle and reloads the WebView
   */
  applyOtaAndReload(): void {
    this.reloadWindow();
  }

  /**
   * Downloads bundle assets and verifies checksum.
   * Can be hooked into by native live-update providers (e.g. Capgo / AppFlow).
   */
  protected async downloadBundle(_otaInfo: OtaBundleInfo): Promise<void> {
    this.otaState.update((state) => ({ ...state, progress: 100 }));
  }

  /**
   * Handles HTTP 426 Upgrade Required error intercepted during an API request
   */
  async handle426Upgrade(upgradeData: any): Promise<void> {
    const latestVersion = upgradeData?.latestClientVersion || '1.0.0';
    const currentVer = this.currentAppVersion;

    this.updateStatus.set('FORCE_UPDATE');

    await this.presentUpdateModal({
      mode: 'FORCE_UPDATE',
      title: 'Update Required',
      message: 'This app version is outdated. Please update to continue using the service.',
      currentVersion: currentVer,
      targetVersion: latestVersion,
      storeUrls: upgradeData?.storeUrls,
    });
  }

  /**
   * Handles HTTP 503 Maintenance error intercepted during an API request
   */
  async handle503Maintenance(maintenanceData: any): Promise<void> {
    this.updateStatus.set('MAINTENANCE');

    await this.presentUpdateModal({
      mode: 'MAINTENANCE',
      title: 'System Maintenance',
      message:
        maintenanceData?.maintenanceMessage ||
        'We are currently undergoing scheduled maintenance. Please check back shortly.',
      currentVersion: this.currentAppVersion,
      targetVersion: this.currentAppVersion,
    });
  }

  /**
   * Polls /api/health/info to re-check update/maintenance status
   */
  async checkUpdate(): Promise<BuildUpdateStatus> {
    try {
      const url = `${environment.baseUrl}/api/health/info`;
      const res = await firstValueFrom(this.http.get<{ isSuccess: boolean; data: ServerInfo }>(url));

      if (res && res.data) {
        this.debugLogger.setServerInfo(res.data);
        return await this.evaluateServerInfo(res.data);
      }
      return 'UP_TO_DATE';
    } catch (err: any) {
      console.warn('⚠️ Build Update Check failed:', err?.message || err);
      return this.updateStatus();
    }
  }

  /**
   * Redirects user to platform store or reloads web app
   */
  openStoreUrl(storeUrls?: { android?: string; ios?: string }): void {
    const platform = Capacitor.getPlatform();
    let targetUrl: string | undefined;

    if (platform === 'android') {
      targetUrl = storeUrls?.android || 'https://play.google.com/store/apps/details?id=com.bm';
    } else if (platform === 'ios') {
      targetUrl = storeUrls?.ios || 'https://apps.apple.com/app/id';
    } else {
      // On Web platform, full browser reload fetches updated assets
      this.reloadWindow();
      return;
    }

    if (targetUrl) {
      this.redirectToStoreUrl(targetUrl, Capacitor.isNativePlatform());
    }
  }

  protected redirectToStoreUrl(targetUrl: string, isNative: boolean): void {
    if (isNative) {
      if (typeof window !== 'undefined') {
        window.open(targetUrl, '_system');
      }
    } else {
      if (typeof window !== 'undefined' && window.location) {
        window.location.href = targetUrl;
      }
    }
  }

  protected reloadWindow(): void {
    if (typeof window !== 'undefined' && window.location) {
      window.location.reload();
    }
  }

  private async presentUpdateModal(props: {
    mode: BuildUpdateStatus;
    title?: string;
    message?: string;
    currentVersion: string;
    targetVersion: string;
    storeUrls?: { android?: string; ios?: string };
  }): Promise<void> {
    if (this.isModalActive()) {
      return;
    }

    try {
      this.isModalActive.set(true);
      const modal = await this.modalService.modalBuildUpdate({
        mode: props.mode,
        title: props.title,
        message: props.message,
        currentVersion: props.currentVersion,
        targetVersion: props.targetVersion,
        storeUrls: props.storeUrls,
      });

      this.activeModalRef = modal;

      if (modal && typeof modal.onDidDismiss === 'function') {
        modal.onDidDismiss().then(() => {
          this.isModalActive.set(false);
          this.activeModalRef = null;
        });
      }
    } catch (err) {
      console.error('Failed to present Build Update Modal:', err);
      this.isModalActive.set(false);
    }
  }

  private dismissActiveModalIfOpen(): void {
    if (this.isModalActive() && this.activeModalRef) {
      try {
        this.modalService.dismiss({}, 'close');
      } catch {
        // Ignored
      }
      this.isModalActive.set(false);
      this.activeModalRef = null;
    }
  }

  private initResumeListener(): void {
    if (this.isResumeListenerAttached) return;
    this.isResumeListenerAttached = true;

    if (Capacitor.isNativePlatform()) {
      App.addListener('appStateChange', (state) => {
        if (state.isActive) {
          this.checkUpdate();
        }
      });
    } else if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.checkUpdate();
        }
      });
    }
  }
}
